import * as THREE from 'three';
import { hasProductLink } from './productLink';

const imageCache = new Map();

const TEXTURE_SIZE = {
  desktop: { width: 1020, height: 520 },
  mobile: { width: 840, height: 480 },
};

const CARD_BORDER = {
  default: 'rgba(95, 188, 198, 0.45)',
  active: 'rgb(95, 188, 198)',
  glow: 'rgba(95, 188, 198, 0.75)',
};

const STATUS_THEME = {
  completed: {
    border: 'rgba(106, 217, 159, 0.5)',
    borderActive: 'rgba(106, 217, 159, 0.82)',
    glow: 'rgba(106, 217, 159, 0.65)',
    spot: 'rgba(106, 217, 159, 0.18)',
    tag: {
      bg: 'rgba(106, 217, 159, 0.12)',
      border: 'rgba(106, 217, 159, 0.5)',
      text: '#6ad99f',
    },
  },
  in_progress: {
    border: 'rgba(255, 209, 102, 0.5)',
    borderActive: 'rgba(255, 209, 102, 0.82)',
    glow: 'rgba(255, 209, 102, 0.65)',
    spot: 'rgba(255, 209, 102, 0.18)',
    tag: {
      bg: 'rgba(255, 209, 102, 0.12)',
      border: 'rgba(255, 209, 102, 0.5)',
      text: '#ffd166',
    },
  },
  planned: {
    border: 'rgba(134, 182, 255, 0.5)',
    borderActive: 'rgba(134, 182, 255, 0.82)',
    glow: 'rgba(134, 182, 255, 0.65)',
    spot: 'rgba(134, 182, 255, 0.18)',
    tag: {
      bg: 'rgba(134, 182, 255, 0.12)',
      border: 'rgba(134, 182, 255, 0.5)',
      text: '#86b6ff',
    },
  },
};

const STATUS_LABEL_STYLE = {
  planned: { text: '#b8d4ff', border: 'rgba(134, 182, 255, 0.45)' },
  in_progress: { text: '#ffe08a', border: 'rgba(255, 209, 102, 0.45)' },
  completed: { text: '#9cefc0', border: 'rgba(106, 217, 159, 0.45)' },
};

const FONT_STACK = '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

const getStatusTheme = (status) => STATUS_THEME[status] || STATUS_THEME.planned;

const getStatusLabelStyle = (status) =>
  STATUS_LABEL_STYLE[status] || STATUS_LABEL_STYLE.planned;

const measureStatusLabel = (ctx, label, fontSize) => {
  const display = label.toUpperCase();
  const paddingX = fontSize * 0.88;
  const paddingY = fontSize * 0.37;
  ctx.font = `500 ${fontSize}px ${FONT_STACK}`;
  if (typeof ctx.letterSpacing === 'string' || ctx.letterSpacing !== undefined) {
    ctx.letterSpacing = `${fontSize * 0.06}px`;
  }
  const textWidth = ctx.measureText(display).width;
  if (ctx.letterSpacing !== undefined) {
    ctx.letterSpacing = '0px';
  }
  const pillW = textWidth + paddingX * 2;
  const pillH = fontSize + paddingY * 2;
  return { display, pillW, pillH, paddingX, paddingY };
};

const drawStatusLabel = (ctx, label, x, y, fontSize, status) => {
  const style = getStatusLabelStyle(status);
  const { display, pillW, pillH, paddingX, paddingY } = measureStatusLabel(ctx, label, fontSize);
  const radius = pillH / 2;

  strokeRoundedRect(ctx, x, y, pillW, pillH, radius);
  ctx.fillStyle = 'rgba(0, 0, 0, 0.35)';
  ctx.fill();
  ctx.strokeStyle = style.border;
  ctx.lineWidth = 1;
  ctx.stroke();

  ctx.font = `500 ${fontSize}px ${FONT_STACK}`;
  if (ctx.letterSpacing !== undefined) {
    ctx.letterSpacing = `${fontSize * 0.06}px`;
  }
  ctx.fillStyle = style.text;
  ctx.fillText(display, x + paddingX, y + paddingY + fontSize * 0.78);
  if (ctx.letterSpacing !== undefined) {
    ctx.letterSpacing = '0px';
  }

  return { pillW, pillH };
};

const strokeRoundedRect = (ctx, x, y, width, height, radius) => {
  if (typeof ctx.roundRect === 'function') {
    ctx.beginPath();
    ctx.roundRect(x, y, width, height, radius);
    return;
  }

  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
};

const loadImage = (src) => {
  if (!src) {
    return Promise.resolve(null);
  }

  if (imageCache.has(src)) {
    return imageCache.get(src);
  }

  const promise = new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });

  imageCache.set(src, promise);
  return promise;
};

const wrapText = (ctx, text, maxWidth, maxLines = 4) => {
  if (!text) {
    return [];
  }

  const words = String(text).split(/\s+/);
  const lines = [];
  let line = '';

  words.forEach((word) => {
    const testLine = line ? `${line} ${word}` : word;
    if (ctx.measureText(testLine).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = testLine;
    }
  });

  if (line) {
    lines.push(line);
  }

  return lines.slice(0, maxLines);
};

const measureCtaButton = (ctx, label, fontSize) => {
  const arrow = '→';
  ctx.font = `500 ${fontSize}px ${FONT_STACK}`;
  const labelWidth = ctx.measureText(label).width;
  const arrowWidth = ctx.measureText(arrow).width;
  const gap = fontSize * 0.18;
  const btnW = labelWidth + arrowWidth + gap;
  const btnH = fontSize * 1.15;
  return { btnW, btnH, arrow, labelWidth, gap };
};

const drawCtaButton = (ctx, label, x, y, fontSize, isActive) => {
  const { btnW, btnH, arrow, labelWidth, gap } = measureCtaButton(ctx, label, fontSize);
  const textY = y + fontSize * 0.95;

  ctx.font = `500 ${fontSize}px ${FONT_STACK}`;
  ctx.fillStyle = isActive ? 'rgb(95, 188, 198)' : 'rgba(95, 188, 198, 0.85)';
  ctx.fillText(label, x, textY);
  ctx.fillText(arrow, x + labelWidth + gap, textY);

  return { btnW, btnH };
};

const drawCoverImage = (ctx, image, dx, dy, dw, dh) => {
  const imgRatio = image.width / image.height;
  const boxRatio = dw / dh;
  let sw;
  let sh;
  let sx;
  let sy;

  if (imgRatio > boxRatio) {
    sh = image.height;
    sw = sh * boxRatio;
    sx = (image.width - sw) / 2;
    sy = 0;
  } else {
    sw = image.width;
    sh = sw / boxRatio;
    sx = 0;
    sy = (image.height - sh) / 2;
  }

  ctx.drawImage(image, sx, sy, sw, sh, dx, dy, dw, dh);
};

const computeCardLayout = (ctx, width, height, cardContent) => {
  const pad = height * 0.048;
  const cardW = width - pad * 2;
  const cardH = height - pad * 2;
  const x = pad;
  const y = pad;
  const contentW = cardW * 0.5;
  const innerX = x + cardW * 0.08;
  const innerW = contentW - cardW * 0.12;

  const titleSize = Math.round(height * 0.09);
  const summarySize = Math.round(height * 0.052);
  const statusLabelSize = Math.round(height * 0.03);
  const ctaSize = Math.round(height * 0.048);
  const visitLabel = cardContent.visitLabel || 'View product';
  const statusLabel = cardContent.statusLabel || '';

  const gapMd = height * 0.038;
  const gapLg = height * 0.05;

  ctx.font = `700 ${titleSize}px ${FONT_STACK}`;
  const titleLines = wrapText(ctx, cardContent.name || 'Product', innerW, 1);
  const titleBlockH = titleLines.length * titleSize * 1.12;

  ctx.font = `400 ${summarySize}px ${FONT_STACK}`;
  const summary = cardContent.summary || '';
  const summaryLines = wrapText(ctx, summary, innerW, 2);
  const summaryBlockH = summaryLines.length * summarySize * 1.38;

  const hasStatusLabel = Boolean(statusLabel);
  const statusBlockH = hasStatusLabel
    ? measureStatusLabel(ctx, statusLabel, statusLabelSize).pillH
    : 0;
  const hasLink = hasProductLink(cardContent.link);

  const { btnW, btnH } = hasLink ? measureCtaButton(ctx, visitLabel, ctaSize) : { btnW: 0, btnH: 0 };

  const contentBlockH =
    titleBlockH +
    (summaryLines.length ? gapMd + summaryBlockH : 0) +
    (hasStatusLabel ? gapMd + statusBlockH : 0) +
    (hasLink ? gapLg + btnH : 0);

  const minTop = y + cardH * 0.11;
  const centeredTop = y + (cardH - contentBlockH) * 0.5;
  const contentTop = Math.max(minTop, centeredTop);

  let cursorY = contentTop;
  const titleBaseline = cursorY + titleSize;
  cursorY += titleBlockH + (summaryLines.length ? gapMd : 0);

  const summaryBaseline = summaryLines.length ? cursorY + summarySize : null;
  if (summaryLines.length) {
    cursorY += summaryBlockH + (hasStatusLabel ? gapMd : 0);
  }

  const statusRowY = hasStatusLabel ? cursorY : null;
  if (hasStatusLabel) {
    cursorY += statusBlockH + (hasLink ? gapLg : 0);
  }

  const ctaY = hasLink ? cursorY : null;
  const ctaAnchor = hasLink
    ? { u: (innerX + btnW / 2) / width, vTop: (ctaY + btnH / 2) / height }
    : null;

  return {
    x,
    y,
    cardW,
    cardH,
    radius: height * 0.072,
    contentW,
    mediaX: x + contentW,
    mediaW: cardW - contentW,
    innerX,
    innerW,
    titleSize,
    titleLines,
    titleBaseline,
    summarySize,
    summaryLines,
    summaryBaseline,
    statusLabelSize,
    statusLabel,
    statusRowY,
    ctaSize,
    visitLabel,
    ctaY,
    hasLink,
    ctaAnchor,
  };
};

let cachedCtaAnchors = {
  desktop: { u: 0.14, vTop: 0.62 },
  mobile: { u: 0.13, vTop: 0.6 },
};

export const getCtaAnchor = (isMobile) =>
  isMobile ? cachedCtaAnchors.mobile : cachedCtaAnchors.desktop;

const drawGlassCard = (ctx, width, height, cardContent, isActive, image) => {
  ctx.clearRect(0, 0, width, height);

  const layout = computeCardLayout(ctx, width, height, cardContent);
  if (layout.ctaAnchor) {
    cachedCtaAnchors[width >= 900 ? 'desktop' : 'mobile'] = layout.ctaAnchor;
  }

  const {
    x,
    y,
    cardW,
    cardH,
    radius,
    contentW,
    mediaX,
    mediaW,
    innerX,
    titleSize,
    titleLines,
    titleBaseline,
    summarySize,
    summaryLines,
    summaryBaseline,
    statusLabelSize,
    statusLabel,
    statusRowY,
    ctaSize,
    visitLabel,
    ctaY,
    hasLink,
  } = layout;

  const statusTheme = getStatusTheme(cardContent.status);

  ctx.save();
  strokeRoundedRect(ctx, x, y, cardW, cardH, radius);
  ctx.clip();

  const base = ctx.createLinearGradient(x, y, x + cardW, y + cardH);
  base.addColorStop(0, '#141b28');
  base.addColorStop(1, '#0a0e14');
  ctx.fillStyle = base;
  ctx.fillRect(x, y, cardW, cardH);

  if (image) {
    ctx.save();
    ctx.beginPath();
    ctx.rect(mediaX, y, mediaW, cardH);
    ctx.clip();
    drawCoverImage(ctx, image, mediaX - mediaW * 0.04, y, mediaW * 1.1, cardH);
    ctx.fillStyle = 'rgba(8, 11, 16, 0.45)';
    ctx.fillRect(mediaX, y, mediaW, cardH);
    const mediaScrim = ctx.createLinearGradient(mediaX, y, x + contentW, y);
    mediaScrim.addColorStop(0, 'rgba(10, 14, 20, 0.98)');
    mediaScrim.addColorStop(0.35, 'rgba(10, 14, 20, 0.72)');
    mediaScrim.addColorStop(1, 'rgba(10, 14, 20, 0.15)');
    ctx.fillStyle = mediaScrim;
    ctx.fillRect(mediaX, y, mediaW, cardH);
    ctx.restore();
  }

  const panel = ctx.createLinearGradient(x, y, x + contentW, y + cardH);
  panel.addColorStop(0, 'rgba(16, 22, 32, 0.98)');
  panel.addColorStop(1, 'rgba(10, 13, 18, 0.96)');
  ctx.fillStyle = panel;
  ctx.fillRect(x, y, contentW + height * 0.04, cardH);

  if (isActive) {
    const spot = ctx.createRadialGradient(
      x + contentW * 0.4,
      y + cardH * 0.2,
      0,
      x + contentW * 0.4,
      y + cardH * 0.2,
      contentW * 0.85
    );
    spot.addColorStop(0, statusTheme.spot);
    spot.addColorStop(1, 'transparent');
    ctx.fillStyle = spot;
    ctx.fillRect(x, y, contentW, cardH);
  }

  ctx.restore();

  ctx.save();
  strokeRoundedRect(ctx, x, y, cardW, cardH, radius);
  ctx.strokeStyle = isActive ? CARD_BORDER.active : CARD_BORDER.default;
  ctx.lineWidth = isActive ? 2 : 1.5;
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(x + radius, y + 1);
  ctx.lineTo(x + cardW - radius, y + 1);
  ctx.strokeStyle = isActive ? CARD_BORDER.glow : CARD_BORDER.default;
  ctx.lineWidth = isActive ? 2 : 1.5;
  ctx.stroke();
  ctx.restore();

  ctx.fillStyle = '#f8fafc';
  ctx.font = `700 ${titleSize}px ${FONT_STACK}`;
  titleLines.forEach((line, index) => {
    ctx.fillText(line, innerX, titleBaseline + index * titleSize * 1.12);
  });

  if (summaryLines.length && summaryBaseline !== null) {
    ctx.fillStyle = 'rgba(196, 208, 220, 0.9)';
    ctx.font = `400 ${summarySize}px ${FONT_STACK}`;
    summaryLines.forEach((line, index) => {
      ctx.fillText(line, innerX, summaryBaseline + index * summarySize * 1.38);
    });
  }

  if (statusLabel && statusRowY !== null) {
    drawStatusLabel(ctx, statusLabel, innerX, statusRowY, statusLabelSize, cardContent.status);
  }

  if (hasLink && ctaY !== null) {
    drawCtaButton(ctx, visitLabel, innerX, ctaY, ctaSize, isActive);
  }
};

export const createCardTexture = (cardContent, isActive, isMobile, theme = 'dark', onUpdate) => {
  const { width, height } = isMobile ? TEXTURE_SIZE.mobile : TEXTURE_SIZE.desktop;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;

  const refresh = (image) => {
    drawGlassCard(ctx, width, height, cardContent, isActive, image);
    texture.needsUpdate = true;
    onUpdate?.();
  };

  refresh(null);
  loadImage(cardContent.image).then(refresh);

  return texture;
};
